import { createFileRoute } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { Lock, ShieldCheck, Sparkles } from "lucide-react";
import { ExerciseBlock } from "@/components/process/ExerciseBlock";
import { PauseGround } from "@/components/process/PauseGround";
import { useProcessWork } from "@/components/process/useProcessWork";
import { SAFETY_EXERCISES } from "@/lib/safety-stabilisation";
import { accentAt, accentVars, ACCENT_CHIP_STYLE, sectionTintStyle } from "@/lib/home-accents";

export const Route = createFileRoute("/safety-stabilisation")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Safety & Stabilisation — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Gentle, member-led exercises for building a sense of safety: grounding plans, safe space, window of tolerance, recognising overwhelm and your own safety toolkit.",
      },
      { property: "og:title", content: "Safety & Stabilisation — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Build a felt sense of safety and notice overwhelm early — private, save-as-you-go exercises you can return to any time.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/safety-stabilisation" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/safety-stabilisation" },
    ],
  }),
  component: withMemberGate(SafetyStabilisationPage, "Safety & Stabilisation"),
});

function SafetyStabilisationPage() {
  const work = useProcessWork("safety_stabilisation");

  return (
    <div className="min-h-screen pb-24" style={sectionTintStyle("sage")}>
      <div className="mx-auto max-w-3xl px-4 pt-6 sm:px-6">

        <header className="mt-4">
          <p className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" aria-hidden /> Process · Safety &
            Stabilisation
          </p>
          <h1 className="mt-3 font-display text-4xl leading-tight text-foreground sm:text-5xl">
            Safety First — Always
          </h1>
          <p className="mt-3 text-[1.02rem] leading-relaxed text-muted-foreground">
            Before going anywhere near deeper emotional material, it helps to know
            what steadies you and how to tell when your nervous system is becoming
            overwhelmed. Nothing here is a test or a treatment — it's simply a place
            to notice what's true for you, in your own time.
          </p>

          <div className="mt-5 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5">
              <Lock className="h-3.5 w-3.5" aria-hidden /> Private to you
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> Saved so you can
              return
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3 py-1.5">
              {work.savedCount} of {SAFETY_EXERCISES.length} started
            </span>
          </div>

          <p className="mt-5 text-xs text-muted-foreground">
            Everything you write is saved automatically to your private account
            when you press "Save for later".
          </p>
        </header>

        <nav aria-label="Exercises" className="mt-7 flex flex-wrap gap-2">
          {SAFETY_EXERCISES.map((exercise, index) => (
            <a
              key={exercise.key}
              href={`#${exercise.key}`}
              style={{ ...accentVars(accentAt(index)), ...ACCENT_CHIP_STYLE }}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition hover:opacity-85"
            >
              <span aria-hidden>{exercise.emoji}</span> {exercise.title}
            </a>
          ))}
        </nav>

        <PauseGround className="mt-7" />

        <div className="mt-7 space-y-6">
          {SAFETY_EXERCISES.map((exercise, index) => (
            <ExerciseBlock
              key={exercise.key}
              exercise={exercise}
              entry={work.forKey(exercise.key)}
              onSave={work.saveEntry}
              isSaving={work.isSaving}
              accent={accentAt(index)}
            />
          ))}
        </div>

        <PauseGround className="mt-8" />
      </div>
    </div>
  );
}
